/**
 * Recipe Chat Service
 * Handles AI chat conversations about specific recipes
 */

import { BaseApiService } from './BaseApiService'
import { RecipeDetailResponse } from '../types/recipe'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3005'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface RecipeContext {
  recipeName: string
  description?: string
  ingredients?: string[]
  instructions?: string[]
  prepTime?: number
  cookTime?: number
  servings?: number
}

interface RecipeChatRequest {
  message: string
  conversationHistory: ChatMessage[]
  recipeContext: RecipeContext
  userId?: string // Required for guest users
}

interface RecipeChatResponse {
  success: boolean
  message: string
  /** Present when the AI detected a reroll action is needed. Discriminate on `type`:
   *  - `ingredient_swap`: swap one ingredient within the same dish
   *  - `full_replace`: replace the entire recipe with a completely different dish
   */
  rerollTrigger?:
    | { type: 'ingredient_swap'; from: string; to: string }
    | { type: 'full_replace'; targetDish: string }
  usage?: {
    inputTokens: number
    outputTokens: number
  }
  error?: string
}

/** Structured result returned by sendMessage — includes the display message plus an optional reroll signal. */
export interface RecipeChatResult {
  message: string
  /** When set, the caller should trigger a recipe reroll. Discriminate on `type`:
   *  - `ingredient_swap`: swap one ingredient within the same dish (`from` → `to`)
   *  - `full_replace`: replace the entire recipe with a completely different dish (`targetDish`)
   */
  rerollTrigger?:
    | { type: 'ingredient_swap'; from: string; to: string }
    | { type: 'full_replace'; targetDish: string }
}

class RecipeChatService extends BaseApiService {
  private readonly MAX_MESSAGE_LENGTH = 500
  private readonly MAX_HISTORY_LENGTH = 10
  private lastRequestTime = 0
  private readonly MIN_REQUEST_INTERVAL = 2000 // 2 seconds between requests

  constructor() {
    super({
      baseUrl: API_BASE_URL,
      defaultTimeout: 30000, // 30s for AI responses
      defaultRetries: 2,
      enableRetry: true
    })
  }

  /**
   * Send a message about a specific recipe.
   * @param recipeId - Recipe UUID
   * @param message - User's message (max 500 chars)
   * @param recipeDetails - Full recipe details for context
   * @param conversationHistory - Previous messages (last 10 will be used)
   * @param userId - User ID (required for guest users)
   * @returns RecipeChatResult — always contains `message`; also contains `rerollTrigger` when
   *   the AI detected a core ingredient swap and a full recipe regeneration is needed.
   */
  async sendMessage(
    recipeId: string,
    message: string,
    recipeDetails: RecipeDetailResponse,
    conversationHistory: ChatMessage[],
    userId?: string
  ): Promise<RecipeChatResult> {
    // Validate message
    this.validateMessage(message)

    // Client-side rate limiting (2s cooldown)
    await this.enforceRateLimit()

    // Build recipe context from full details
    const recipeContext: RecipeContext = {
      recipeName: recipeDetails.recipe.name,
      description: recipeDetails.recipe.description,
      ingredients: recipeDetails.recipe.ingredients?.map((ing) =>
        [ing.amount, ing.unit, ing.name].filter(Boolean).join(' ')
      ),
      instructions: recipeDetails.recipe.instructions,
      prepTime: recipeDetails.recipe.prepTime,
      cookTime: recipeDetails.recipe.cookTime,
      servings: recipeDetails.recipe.servings
    }

    // Trim conversation history to last 10 messages
    const trimmedHistory = this.trimHistory(conversationHistory)

    // Build request body
    const requestBody: RecipeChatRequest = {
      message: message.trim(),
      conversationHistory: trimmedHistory,
      recipeContext,
      userId // Include userId for guest users (access verification)
    }

    try {
      const response = await this.post<RecipeChatResponse>(
        `/api/recipes/${recipeId}/chat`,
        requestBody
      )

      if (!response.success) {
        throw new Error(response.error || 'Failed to get response')
      }

      return {
        message: response.message,
        ...(response.rerollTrigger ? { rerollTrigger: response.rerollTrigger } : {})
      }
    } catch (error: any) {
      // Handle rate limit errors
      if (error.message?.includes('Rate limit') || error.message?.includes('429')) {
        throw new Error('You\'re sending messages too quickly. Please wait a moment.')
      }

      // Handle access denied errors
      if (error.message?.includes('403') || error.message?.includes('access denied')) {
        throw new Error('You do not have permission to chat about this recipe.')
      }

      // Generic error
      throw new Error('Failed to get response. Please try again.')
    }
  }

  /**
   * Validate message meets requirements
   */
  private validateMessage(message: string): void {
    if (!message || message.trim().length === 0) {
      throw new Error('Message cannot be empty')
    }

    if (message.length > this.MAX_MESSAGE_LENGTH) {
      throw new Error(`Message must be ${this.MAX_MESSAGE_LENGTH} characters or less`)
    }
  }

  /**
   * Trim conversation history to most recent N messages
   */
  private trimHistory(history: ChatMessage[]): ChatMessage[] {
    return history.slice(-this.MAX_HISTORY_LENGTH)
  }

  /**
   * Enforce client-side rate limiting (2s cooldown between messages)
   */
  private async enforceRateLimit(): Promise<void> {
    const now = Date.now()
    const timeSinceLastRequest = now - this.lastRequestTime

    if (timeSinceLastRequest < this.MIN_REQUEST_INTERVAL) {
      const waitTime = this.MIN_REQUEST_INTERVAL - timeSinceLastRequest
      await new Promise(resolve => setTimeout(resolve, waitTime))
    }

    this.lastRequestTime = Date.now()
  }
}

// Export singleton instance
export const recipeChatService = new RecipeChatService()

// Also export the class for testing
export default RecipeChatService
